import './index.scss'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import Footer from '../../components/layouts/footer'
import {useDispatch, useSelector} from 'react-redux'
import {SimpleBlueButton} from '../../components/core/button'
import {CheckIcon, RejectedIcon, TaskDoneIcon} from '../../icons'
import {routes} from '../../routes'
import {Link, useLocation, useSearchParams} from 'react-router-dom'
import {setSessionIdExist} from '../../stores/sidebar'
import {getSubscriptions, getSubscriptionPayments} from '../../stores/payment'
import {useEffect, useState} from 'react'
import TagManager from 'react-gtm-module'
import tagManager from '../../data/tagManager.json'
import AmountManager from '../../utils/amount'
import {colors} from '../../utils/colors'
import classNames from 'classnames'
import NavigationHelper from '../../utils/navigation'
import {useNamespaceTranslations} from '../../utils/hooks/useNamespacesTranslations'

const Subscription = () => {
    const dispatch = useDispatch()
    const location = useLocation()
    const text = useNamespaceTranslations('subscription')
    const [searchParams] = useSearchParams()
    const globalInfo = useSelector(state => state.sidebar.globalInfo)
    const subscriptions = useSelector(state => state.payment.subscriptions)
    const paymentData = useSelector(state => state.payment.paymentData)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [activeSubscription, setActiveSubscription] = useState({})
    const mobileWidth = 992
    const iconWidth = 229
    const iconHeight = 193
    const isMobile = window.innerWidth < mobileWidth
    const isSuccessPage = location.pathname.includes('success')
    const isUpdating = location.pathname.includes('update')

    const timelineSteps = [
        {
            name: text.timeline_step_1,
            is_passed: true
        },
        {
            name: text.timeline_step_2,
            is_passed: true
        },
        {
            name: text.timeline_step_3,
            is_passed: !globalInfo?.length > 0
        }
    ]

    useEffect(() => {
        dispatch(getSubscriptions())
        searchParams.get('session_id')?.length > 0 && dispatch(setSessionIdExist(true))

        if (isSuccessPage && Object.values(paymentData).length > 0) {
            activeSubscription?.in_trial ? TagManager.dataLayer({
                dataLayer: {
                    event: tagManager.events.formSubmission,
                    formAction: tagManager.formActions.trial
                }
            }) : TagManager.dataLayer({
                dataLayer: {
                    event: tagManager.events.purchase,
                    ecommerce: {
                        transaction_id: activeSubscription?.subscription_id,
                        value: AmountManager.normalizePrice(paymentData?.data?.subscription?.payments[0]?.paid_amount),
                        tax: 0,
                        currency: paymentData?.data?.subscription?.payments[0]?.currency.toUpperCase(),
                        items: [
                            {
                                item_name: paymentData?.data?.subscription?.plan,
                                price: AmountManager.normalizePrice(paymentData?.data?.subscription?.payments[0]?.paid_amount),
                                quantity: 1
                            }
                        ]
                    }
                }
            })
        }
    }, [isSuccessPage, JSON.stringify(paymentData)])

    useEffect(() => {
        setActiveSubscription(subscriptions?.data?.subscriptions?.find(item => item?.active === true))
    }, [subscriptions])

    useEffect(() => {
        activeSubscription?.subscription_id && dispatch(getSubscriptionPayments(activeSubscription?.subscription_id))
    }, [activeSubscription])

    return (
        <main className={classNames('main subscription', {
            dark: isDarkMode
        })}>
            <div className="content-container">
                <ResponsivePageTitle/>
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p className={classNames('section', {
                                    dark: isDarkMode
                                })}>{text.section}</p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1  className={classNames('page-title', {
                                    dark: isDarkMode
                                })}>{
                                        (isSuccessPage && !isUpdating) ?
                                            text.success_title :
                                            (isSuccessPage && isUpdating) ?
                                                text.successfully_updated :
                                                (!isSuccessPage && isUpdating) ?
                                                    text.not_updated :
                                                    text.cancel_title
                                    }</h1>
                            </PageTitle>
                        </div>
                    </div>
                </div>
                <div className={classNames('content-body', {
                    dark: isDarkMode
                })}>
                    <div className="left">
                        <div className="info">
                            <p className="subscription-desc">
                                {
                                    (isSuccessPage && !isUpdating) ?
                                        text.success_desc :
                                        (isSuccessPage && isUpdating) ?
                                            text.successfully_updated_message :
                                            (!isSuccessPage && isUpdating) ?
                                                text.not_updated_message :
                                                text.cancel_desc
                                }
                            </p>
                        </div>
                        {(isSuccessPage && !isUpdating) &&
                            (
                                <>
                                    <div className="timeline-container">
                                        <ul className={`timeline ${globalInfo ? 'bg-gradient' : ''}`}>
                                            {timelineSteps.map((step, index) => (
                                                <li key={index}>
                                                    <CheckIcon fill={!step.is_passed ? colors.colorLightGrey : ''}
                                                        stroke={!step.is_passed ? colors.colorBgFilter : ''}/>
                                                    <span
                                                        className={`step-text ${!step.is_passed ? 'disable' : ''}`}>{step.name}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <ul className="status-points">
                                        {timelineSteps.map((step, index) => (
                                            <li className="status" key={index}>
                                                <CheckIcon fill={!step.is_passed ? colors.colorLightGrey : ''}
                                                    stroke={!step.is_passed ? colors.colorBgFilter : ''}/>
                                                <span
                                                    className={`step-text ${!step.is_passed ? 'disable' : ''}`}>{step.name}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )
                        }
                        <div className={`buttons ${(!isSuccessPage && !isUpdating) ? 'margin-top-30' : ''}`}>
                            <Link
                                to={NavigationHelper.getLocalizedPath(isUpdating ?
                                    routes.subscriptions.paths :
                                    isSuccessPage ?
                                        routes.subscriptions.paths :
                                        routes.plans.paths) }>
                                <SimpleBlueButton>{isUpdating ? text.billings : isSuccessPage ? text.billings : text.plans}</SimpleBlueButton>
                            </Link>
                            <Link to={NavigationHelper.getLocalizedPath(isUpdating ?
                                routes.payment_methods.paths :
                                routes.resources.paths)}>
                                <SimpleBlueButton
                                    outline={true}>{isUpdating ? text.payment_methods : text.home}</SimpleBlueButton>
                            </Link>
                        </div>
                    </div>
                    <div className="right">
                        {
                            isSuccessPage ?
                                <TaskDoneIcon width={isMobile && iconWidth} height={isMobile && iconHeight}/> :
                                <RejectedIcon width={isMobile && iconWidth} height={isMobile && iconHeight}/>
                        }
                    </div>
                </div>
            </div>
            <Footer/>
        </main>
    )
}

export default Subscription
